import { z } from 'zod';
import { Id } from '../primitives.js';

/**
 * Контракты сервиса `notifications` — то, что видит клиника: журнал доставки,
 * настройки каналов, квота SMS. Заявка на отправку и попытка провайдера живут
 * только в базе сервиса; наружу отдаётся проекция, необходимая интерфейсу, не
 * состав заявки целиком.
 *
 * `push` доставляется двумя путями: тостом через уже открытый WebSocket
 * `sync-gateway`, пока вкладка открыта, и Web Push через браузер и сервис-
 * воркер — второй путь требует регистрации подписки (`RegisterPushDeviceRequest`
 * ниже) и работает, даже когда вкладка закрыта. Настоящий OS-push для
 * мобильных сборок (FCM/APNs) — веха 2.5, отдельный провайдер поверх той же
 * таблицы подписок.
 *
 * Документация модуля: docs/03-modules/10-notifications.md
 */

export const NotificationChannel = z.enum(['push', 'telegram', 'sms', 'email']);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

export const RecipientKind = z.enum(['staff', 'patient']);
export type RecipientKind = z.infer<typeof RecipientKind>;

export const NotificationState = z.enum([
  'queued',
  'sending',
  'sent',
  'delivered',
  'failed',
  'retrying',
  'permanently_failed',
  'suppressed',
]);
export type NotificationState = z.infer<typeof NotificationState>;

export const NotificationRequestView = z.object({
  id: Id,
  recipientKind: RecipientKind,
  recipientId: Id,
  /** Пациент, к которому относится заявка — пусто у сотруднических уведомлений. */
  patientId: Id.nullable(),
  templateId: z.string(),
  state: NotificationState,
  /** Канал, на котором сейчас стоит заявка — последний из каскада, что пробовался. */
  channel: NotificationChannel.nullable(),
  suppressionReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type NotificationRequestView = z.infer<typeof NotificationRequestView>;

export const DeliveryAttemptView = z.object({
  id: Id,
  requestId: Id,
  channel: NotificationChannel,
  attemptNumber: z.number().int().positive(),
  outcome: z.enum(['sent', 'failed']),
  providerError: z.string().nullable(),
  attemptedAt: z.iso.datetime(),
});
export type DeliveryAttemptView = z.infer<typeof DeliveryAttemptView>;

export const NotificationsQuery = z.object({
  patientId: Id.optional(),
  state: NotificationState.optional(),
});
export type NotificationsQuery = z.infer<typeof NotificationsQuery>;

export const NotificationsList = z.object({ requests: z.array(NotificationRequestView) });
export type NotificationsList = z.infer<typeof NotificationsList>;

export const NotificationHistory = z.object({ attempts: z.array(DeliveryAttemptView) });
export type NotificationHistory = z.infer<typeof NotificationHistory>;

/** Настройка канала — своя (у любого сотрудника) или клиники целиком (владелец/админ). */
export const ChannelPreference = z.object({
  channel: NotificationChannel,
  enabled: z.boolean(),
});
export type ChannelPreference = z.infer<typeof ChannelPreference>;

export const ChannelPreferences = z.object({ preferences: z.array(ChannelPreference) });
export type ChannelPreferences = z.infer<typeof ChannelPreferences>;

export const UpdateChannelPreferenceRequest = z.object({
  channel: NotificationChannel,
  enabled: z.boolean(),
});
export type UpdateChannelPreferenceRequest = z.infer<typeof UpdateChannelPreferenceRequest>;

export const SmsQuotaView = z.object({
  monthlyLimit: z.number().int().positive(),
  sentThisMonth: z.number().int().nonnegative(),
  periodEndsAt: z.iso.datetime(),
});
export type SmsQuotaView = z.infer<typeof SmsQuotaView>;

// --- Web Push: подписка браузера -----------------------------------------------

/**
 * Ключи подписки ровно в том виде, в котором их отдаёт браузерный
 * `PushSubscription.toJSON()` — лишнего разбора на клиенте нет, сервер
 * принимает объект как есть.
 */
export const RegisterPushDeviceRequest = z.object({
  endpoint: z.url(),
  keys: z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
  }),
  /** Для разбора инцидента: какой браузер завёл подписку, если их несколько. */
  userAgent: z.string().max(300).optional(),
});
export type RegisterPushDeviceRequest = z.infer<typeof RegisterPushDeviceRequest>;

export const UnregisterPushDeviceRequest = z.object({ endpoint: z.url() });
export type UnregisterPushDeviceRequest = z.infer<typeof UnregisterPushDeviceRequest>;

export const VapidPublicKeyView = z.object({ publicKey: z.string().min(1).nullable() });
export type VapidPublicKeyView = z.infer<typeof VapidPublicKeyView>;
